/**
 * Resolve the voice working-context hint from FE UI state.
 *
 * On Heidi Work the route has no session id (`/scribe/chat/projects/...`), so
 * the clinician's "current session" is whatever the canvas / composer is
 * focused on — not `getCurrentSessionId()`.
 */

import type { LinkedContextItem } from '../../contracts/projects/linked-context';
import type { VoiceContextHint } from '../../contracts/voice-protocol';
import type { Artifact } from '../../data/projects/artifacts/state';
import type { VoiceActiveTabSnapshot } from './store/voiceActiveTabAtom';

const SESSION_ARTIFACT_TYPES = new Set(['session', 'note', 'tasks', 'codes', 'document']);

/** Ids carried by the open Work canvas artifact, if any. */
export const hintFromOpenArtifact = (
  artifact: Artifact | null | undefined
): Pick<VoiceContextHint, 'sessionId' | 'patientProfileId'> => {
  if (!artifact) return {};
  if (SESSION_ARTIFACT_TYPES.has(artifact.type) && 'sessionId' in artifact) {
    return { sessionId: artifact.sessionId };
  }
  if (artifact.type === 'patient') {
    return { patientProfileId: artifact.patientId };
  }
  return {};
};

/** Ids from the Work composer linked chip (single-slot list). */
export const hintFromLinkedItems = (
  items: readonly LinkedContextItem[] | null | undefined
): Pick<VoiceContextHint, 'sessionId' | 'patientProfileId'> => {
  if (!items?.length) return {};
  for (const item of items) {
    if (item.type === 'session') {
      return { sessionId: item.sessionId };
    }
    if (item.type === 'patient' && item.patientId) {
      return { patientProfileId: item.patientId };
    }
  }
  return {};
};

export type BuildVoiceContextHintInput = {
  pathname: string;
  /** Route session id from `/scribe/session(s)/:id` — empty on Work. */
  routeSessionId?: string | null;
  /** Route patient id from `/patients/:id` — empty off the patient page. */
  routePatientProfileId?: string | null;
  openArtifact?: Artifact | null;
  linkedItems?: readonly LinkedContextItem[] | null;
  /** Active tab on classic Scribe (from session UI sync). */
  activeTab?: VoiceActiveTabSnapshot | null;
};

const isNonEmptyHint = (
  hint: Pick<VoiceContextHint, 'sessionId' | 'patientProfileId'>
): boolean => Boolean(hint.sessionId || hint.patientProfileId);

/**
 * Resolve ids for the working context.
 *
 * Classic Scribe: the route session is the source of truth. A leftover Work
 * canvas ``patient`` / session artifact must never wipe it — that made Talk
 * to Heidi claim "no active session" while a patient canvas was open on screen.
 *
 * Heidi Work: open canvas artifact → composer linked chip → route (rare).
 * Take the full id pair from the first non-empty source so a patient canvas
 * cannot blend with a lingering linked-session chip (or vice versa).
 */
export const buildVoiceContextHint = (
  input: BuildVoiceContextHintInput
): VoiceContextHint => {
  const surface = input.pathname.startsWith('/scribe/chat/projects') ? 'work' : 'scribe';
  const fromArtifact = hintFromOpenArtifact(input.openArtifact);
  const fromLinked = hintFromLinkedItems(input.linkedItems);
  const routeSessionId = input.routeSessionId?.trim() || undefined;
  const routePatientProfileId = input.routePatientProfileId?.trim() || undefined;
  const fromRoute: Pick<VoiceContextHint, 'sessionId' | 'patientProfileId'> = {
    ...(routeSessionId ? { sessionId: routeSessionId } : {}),
    ...(routePatientProfileId ? { patientProfileId: routePatientProfileId } : {}),
  };

  let ids: Pick<VoiceContextHint, 'sessionId' | 'patientProfileId'> = fromRoute;
  if (surface === 'work') {
    if (isNonEmptyHint(fromArtifact)) {
      ids = fromArtifact;
    } else if (isNonEmptyHint(fromLinked)) {
      ids = fromLinked;
    }
  } else if (routeSessionId) {
    // Session route wins over a leftover /patients id in query/state.
    ids = { sessionId: routeSessionId };
  }

  const tab = input.activeTab;
  const tabMatchesSession = tab && (!ids.sessionId || tab.sessionId === ids.sessionId);

  return {
    ...(ids.sessionId ? { sessionId: ids.sessionId } : {}),
    ...(ids.patientProfileId ? { patientProfileId: ids.patientProfileId } : {}),
    surface,
    ...(tabMatchesSession && tab.tabKind ? { tabKind: tab.tabKind } : {}),
    ...(tabMatchesSession && tab.documentId ? { documentId: tab.documentId } : {}),
    ...(tabMatchesSession && tab.templateId ? { templateId: tab.templateId } : {}),
    ...(tabMatchesSession && tab.templateName ? { templateName: tab.templateName } : {}),
    ...(tabMatchesSession && tab.voiceStyle ? { voiceStyle: tab.voiceStyle } : {}),
  };
};

export const voiceContextHintKey = (hint: VoiceContextHint): string =>
  [
    hint.surface ?? '',
    hint.sessionId ?? '',
    hint.patientProfileId ?? '',
    hint.tabKind ?? '',
    hint.documentId ?? '',
    hint.templateId ?? '',
    hint.templateName ?? '',
    hint.voiceStyle ?? '',
  ].join('|');
